import { isNullish } from './conditions.util'

/**
 * Creates an error when a value does not match expected primitive types.
 *
 * @function
 * @private
 * @param {unknown} value - The value to check
 * @param {string[]} types - Array of expected type strings (e.g., 'boolean', 'string')
 * @returns {Error} An error object with a descriptive message
 * @example
 * createAssertPrimitivesError(123, ['string']) // Returns Error: Expected value 123 to be type of 'string' but got 'number'
 */
export const createAssertPrimitivesError = (value: unknown, types: string[]) =>
  Error(
    `Expected value ${value} to be type of ${types.map((type) => `'${type}'`).join(' | ')} but got '${typeof value}'`
  )

/**
 * Asserts that the given value is a boolean.
 *
 * @function
 * @param {unknown} value - The value to check
 * @throws {Error} Throws an error if the value is not a boolean
 * @example
 * assertBoolean(true); // Passes
 * assertBoolean('string'); // Throws Error: Expected value string to be type of 'boolean' but got 'string'
 */
export const assertBoolean: (value: unknown) => void = (value) => {
  const type = typeof value
  if (type !== 'boolean') throw createAssertPrimitivesError(value, ['boolean'])
}

/**
 * Asserts that the given value is a string.
 *
 * @function
 * @param {unknown} value - The value to check
 * @throws {Error} Throws an error if the value is not a string
 * @example
 * assertString('hello'); // Passes
 * assertString(123); // Throws Error: Expected value 123 to be type of 'string' but got 'number'
 */
export const assertString: (value: unknown) => void = (value) => {
  const type = typeof value
  if (type !== 'string') throw createAssertPrimitivesError(value, ['string'])
}

/**
 * Asserts that the given value is a number.
 *
 * @function
 * @param {unknown} value - The value to check
 * @throws {Error} Throws an error if the value is not a number
 * @example
 * assertNumber(42); // Passes
 * assertNumber('number'); // Throws Error: Expected value number to be type of 'number' but got 'string'
 */
export const assertNumber: (value: unknown) => void = (value) => {
  const type = typeof value
  if (isNullish(value) || type !== 'number') throw createAssertPrimitivesError(value, ['number'])
}

/**
 * Asserts that the given value matches one of the specified primitive types.
 *
 * @function
 * @param {unknown} value - The value to check
 * @param {...string[]} types - Variable number of expected type strings (e.g., 'boolean', 'number')
 * @throws {Error} Throws an error if the value does not match any of the specified types
 * @example
 * assertPrimitives(123, 'number'); // Passes
 * assertPrimitives('hello', 'string'); // Passes
 * assertPrimitives(true, 'boolean'); // Passes
 * assertPrimitives(null, 'number', 'string'); // Throws Error: Expected value null to be type of 'number' | 'string' but got 'object'
 */
export const assertPrimitives = (value: unknown, ...types: string[]) => {
  const type = typeof value
  if (!types.includes(type)) throw createAssertPrimitivesError(value, types)
}

/**
 * Asserts that the given value is neither null nor undefined.
 *
 * @function
 * @param {unknown} value - The value to check
 * @throws {Error} Throws an error if the value is null or undefined
 * @example
 * assertNotNullish('test'); // Passes
 * assertNotNullish(null); // Throws Error: Expected value to be defined but got null
 * assertNotNullish(undefined); // Throws Error: Expected value to be defined but got undefined
 */
export const assertNotNullish = (value: unknown) => {
  if (isNullish(value)) throw `Expected value to be defined but got ${value}`
}
